/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { sizes, Spacer, TypeRampV2 } from '@smartsheet/lodestar-core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  isExtensionVisible,
  isModuleVisible,
} from '../../../../../services/api/ExtensionService';
import { GraphNodeTypes } from '../../../../../services/ducks/designerDuck/designerDuck';
import {
  selectAllExtensions,
  selectExtensionRegistrationData,
} from '../../../../../services/ducks/extensionsDuck/extensionsDuck';
import { selectAllModules } from '../../../../../services/ducks/modulesDuck/modulesDuck';
import { selectProviderFeatures } from '../../../../../services/ducks/providerDuck/providerDuck';
import { AddModuleListItem } from '../AddModuleListItem';

const subheadingStyle = css`
  padding-bottom: ${sizes.small}px;
`;

const sectionStyle = css`
  margin-bottom: ${sizes.large}px;
`;

interface AddModuleListProps {
  searchTerm: string;
}

export const AddModuleList = ({ searchTerm }: AddModuleListProps) => {
  const extensions = useSelector(selectAllExtensions);
  const modules = useSelector(selectAllModules);
  const providerFeatures = useSelector(selectProviderFeatures);

  const isSearching = searchTerm.length > 0;

  const extensionRegistrationData = useSelector(
    selectExtensionRegistrationData
  );

  const { t: translate } = useTranslation();

  const pinned: JSX.Element[] = [
    <AddModuleListItem
      key="state"
      label={translate('common:state')}
      type={GraphNodeTypes.STATE}
    />,
    <AddModuleListItem
      key="junction"
      label={translate('common:junction')}
      type={GraphNodeTypes.JUNCTION}
    />,
    <AddModuleListItem
      key="child"
      label={translate('Child Workflow', { ns: 'common' })}
      type={GraphNodeTypes.CHILD}
    />,
  ];
  const integrations: JSX.Element[] = [];
  const utilities: JSX.Element[] = [];
  const custom: JSX.Element[] = [];

  extensions.forEach(extension => {
    if (
      isExtensionVisible(extension, providerFeatures, extensionRegistrationData)
    ) {
      const extensionModules: JSX.Element[] = [];
      const re = new RegExp(searchTerm, 'i');

      modules.forEach(module => {
        if (module.pluginUUID === extension.objectUUID) {
          if (
            module.name.match(re) &&
            isModuleVisible(module, extension, providerFeatures)
          ) {
            extensionModules.push(
              <AddModuleListItem
                key={module.id}
                label={module.name || module.id}
                moduleID={module.id}
                extensionUUID={extension.objectUUID}
                type={
                  module.transits || module.exits?.length
                    ? GraphNodeTypes.GROUP_JUNCTION
                    : GraphNodeTypes.MODULE
                }
              />
            );
          }
        }
      });

      if (extensionModules.length) {
        if (extension.category === 'Utilities') {
          utilities.push(
            <AddModuleListItem
              key={extension.objectUUID}
              label={extension.displayName || ''}
              logoName={extension.logoName}
              isOpen={isSearching}
              type={GraphNodeTypes.MODULE}
            >
              {extensionModules}
            </AddModuleListItem>
          );
        } else if (extension.category === 'User') {
          custom.push(
            <AddModuleListItem
              key={extension.objectUUID}
              label={extension.displayName || ''}
              logoName={extension.logoName}
              isOpen={isSearching}
              type={GraphNodeTypes.MODULE}
            >
              {extensionModules}
            </AddModuleListItem>
          );
        } else {
          integrations.push(
            <AddModuleListItem
              key={extension.objectUUID}
              label={extension.displayName || ''}
              logoName={extension.logoName}
              isOpen={isSearching}
              type={GraphNodeTypes.MODULE}
            >
              {extensionModules}
            </AddModuleListItem>
          );
        }
      }
    }
  });

  if (integrations.length + utilities.length + custom.length > 0) {
    return (
      <div
        css={css`
          user-select: none;
        `}
        data-pa11y-id="add-module-list"
      >
        <div css={sectionStyle}>
          <TypeRampV2
            system="lodestar"
            variant="metadata"
            css={subheadingStyle}
          >
            {translate('designer:addModule.pinned')}
          </TypeRampV2>
          <Spacer orientation="column" space="xxSmall">
            {pinned}
          </Spacer>
        </div>
        <div css={sectionStyle}>
          {isSearching ? null : (
            <TypeRampV2
              system="lodestar"
              variant="metadata"
              css={subheadingStyle}
            >
              {translate('designer:addModule.integrations')}
            </TypeRampV2>
          )}
          <Spacer orientation="column" space="xxSmall">
            {integrations}
          </Spacer>
        </div>
        <div css={sectionStyle}>
          {isSearching ? null : (
            <TypeRampV2
              system="lodestar"
              variant="metadata"
              css={subheadingStyle}
            >
              {translate('designer:addModule.utilities')}
            </TypeRampV2>
          )}
          <Spacer orientation="column" space="xxSmall">
            {utilities}
          </Spacer>
        </div>
        {isSearching ? null : (
          <TypeRampV2
            system="lodestar"
            variant="metadata"
            css={subheadingStyle}
          >
            {translate('designer:addModule.custom')}
          </TypeRampV2>
        )}
        <Spacer orientation="column" space="xxSmall">
          {custom}
        </Spacer>
      </div>
    );
  } else {
    return (
      <React.Fragment>
        <TypeRampV2 system="lodestar" variant="metadata">
          {translate('designer:addModule.noSearchResults')}
        </TypeRampV2>
      </React.Fragment>
    );
  }
};
